import {
  ConfigurationType,
  ConfigurationToName,
  SelectedDependencyWithClearHistoryProps,
} from './types';
import { Fragment, useMemo } from 'react';
import { DialogLoader } from '../../common/dialogs/DialogLoader';
import Box from '@mui/material/Box';
import { DefaultConfigurationTable } from '../configuration-item-tables/DefaultConfigurationTable';
import { FieldsTable } from '../configuration-item-tables/FieldsTable';
import { FlowsTable } from '../configuration-item-tables/FlowsTable';
import { ObjectsTable } from '../configuration-item-tables/ObjectsTable';
import { TableHeaderVariant } from '../configuration-item-tables/shared/TableWithHeader';
import { setSfFieldName } from '../../pages/rollups/rollupHelpers';
import { FieldLabelWithNameInTooltip } from '../shared/FieldLabelWithNameInTooltip';
import { CpqTables } from '../configuration-item-tables/CpqTables';
import { isItemActive } from '../helpers';
import { useSelector } from 'react-redux';
import { selectSaltoObjectTypes } from '../../../reducers/global/globalReducer';
import groupBy from 'lodash/groupBy';
import { translateNewConfigurationTypeKeysToOldVersion } from './helpers';
import { RelatedObjectsTable } from '../configuration-item-tables/RelatedObjectsTable';
import { PossibleSectionInDependencies } from './search/types';
import { createStableLabelWrapper } from './search/LabelWrapperComponent';

interface DependenciesTableItem {
  id: string;
  name: string;
  lastModified: string;
  label: string;
  parentType: string;
  type: string;
  metadataId: string;
  filename: string;
  objectName?: string;
  objectLabel?: string;
  flowType?: string;
  isRollup?: boolean;
  isActive: boolean;
}

export interface DependenciesTableMap {
  [key: string]: DependenciesTableItem[];
}

interface DependenciesTableProps {
  configurationItems: DependenciesTableMap;
  onChooseNewRule: (props: SelectedDependencyWithClearHistoryProps) => void;
  listViewCounter?: number;
  isLoadingDependency?: boolean;
  activeItemId?: string;
  crmOrgId: string;
  clearHistory?: boolean;
  section?: PossibleSectionInDependencies;
}

export const DependenciesTable = ({
  configurationItems,
  onChooseNewRule,
  listViewCounter,
  isLoadingDependency,
  activeItemId,
  clearHistory,
  crmOrgId,
  section = 'dependencies',
}: DependenciesTableProps) => {
  const saltoObjectTypes = useSelector(selectSaltoObjectTypes(crmOrgId));

  const configurationKeys = useMemo(
    () => (configurationItems ? Object.keys(configurationItems) : []),
    [configurationItems],
  );

  const renderedTables = useMemo(() => {
    if (!configurationItems) {
      return [];
    }
    return configurationKeys.map((confKey, idx) => {
      const key = confKey as ConfigurationType;
      const items = configurationItems[key];

      if (!items.length) {
        return <Fragment key={`no-configuration-items_${idx}`} />;
      }

      // Handle the 'others' case separately to avoid extra nesting
      if (key === ConfigurationType.others) {
        const itemsWithTranslatedKeys = items.map((item) => ({
          ...item,
          type: translateNewConfigurationTypeKeysToOldVersion(item.type as string),
        }));
        const groupedItems = groupBy(itemsWithTranslatedKeys, 'type');

        return (
          <DependenciesTable
            key={key}
            configurationItems={groupedItems}
            onChooseNewRule={onChooseNewRule}
            listViewCounter={listViewCounter}
            isLoadingDependency={isLoadingDependency}
            activeItemId={activeItemId}
            clearHistory={clearHistory}
            crmOrgId={crmOrgId}
            section={section}
          />
        );
      }

      const createTableContent = () => {
        switch (key) {
          case ConfigurationType.fields: {
            // Create stable wrapper component ONCE per table, not per row
            // This wrapper accepts rowIndex as a prop to avoid creating new components
            const StableFieldLabelWrapper = createStableLabelWrapper({
              field: 'label',
              section,
              tableType: 'fields',
            });

            return (
              <FieldsTable
                key={key}
                onFieldClick={onChooseNewRule}
                items={items.map((item, index) => ({
                  id: item.id ?? setSfFieldName(item.name, item.objectName ?? ''),
                  name: item.name,
                  objectName: item.objectName ?? '',
                  objectLabel:
                    saltoObjectTypes?.find((type) => type.objectType === item.objectName)?.label ??
                    item.objectName ??
                    '',
                  label: (
                    <FieldLabelWithNameInTooltip
                      key={item.id}
                      label={item.label}
                      name={item.name}
                      LabelWrapperComponent={StableFieldLabelWrapper}
                      labelWrapperComponentProps={{ rowIndex: index }}
                    />
                  ),
                  type: item.type as any, //to display in the table any relevant field type
                  isRollup: !!item.isRollup,
                }))}
                headerVariant={TableHeaderVariant.expandable}
                activeItemId={activeItemId}
                clearHistory={clearHistory}
                tableHeader="Fields"
                section={section}
              />
            );
          }

          case ConfigurationType.rollups: {
            // Create stable wrapper component ONCE per table, not per row
            // This wrapper accepts rowIndex as a prop to avoid creating new components
            const StableRollupLabelWrapper = createStableLabelWrapper({
              field: 'label',
              section,
              tableType: 'rollups',
            });

            return (
              <FieldsTable
                key={key}
                onFieldClick={onChooseNewRule}
                items={items.map((item, index) => ({
                  id: item.id ?? setSfFieldName(item.name, item.objectName ?? ''),
                  name: item.name,
                  objectName: item.objectName ?? '',
                  objectLabel:
                    saltoObjectTypes?.find((type) => type.objectType === item.objectName)?.label ??
                    item.objectName ??
                    '',
                  label: (
                    <FieldLabelWithNameInTooltip
                      key={item.id}
                      label={item.label}
                      name={item.name}
                      LabelWrapperComponent={StableRollupLabelWrapper}
                      labelWrapperComponentProps={{ rowIndex: index }}
                    />
                  ),
                  type: item.type as any, //to display in the table any relevant field type
                  isRollup: !!item.isRollup,
                }))}
                headerVariant={TableHeaderVariant.expandable}
                activeItemId={activeItemId}
                clearHistory={clearHistory}
                tableHeader="Rollups"
                dataTableType="rollups"
                section={section}
              />
            );
          }

          case ConfigurationType.layouts:
            return (
              <DefaultConfigurationTable
                headerVariant={TableHeaderVariant.expandable}
                tableHeader={ConfigurationToName[key]}
                items={items}
                onItemClick={onChooseNewRule}
                key={key}
                parentType={key}
                activeItemId={activeItemId}
                clearHistory={clearHistory}
                section={section}
              />
            );

          case ConfigurationType.flows:
            return (
              <FlowsTable
                key={key}
                items={items.map((item) => ({
                  id: item.id,
                  name: item.name,
                  label: item.label,
                  lastModified: item.lastModified,
                  type: item.flowType ?? item.type ?? '',
                  objectName: item.objectName ?? '',
                  isActive: isItemActive(item),
                }))}
                onFlowClick={onChooseNewRule}
                headerVariant={TableHeaderVariant.expandable}
                activeItemId={activeItemId}
                clearHistory={clearHistory}
                section={section}
              />
            );

          case ConfigurationType.relatedObjects:
            return (
              <RelatedObjectsTable
                key={key}
                items={items.map((item) => ({
                  id: item.id,
                  name: item.name,
                  label: item.label,
                  parentType: item.parentType as string,
                  dependencyType: item.type as string,
                  objectName: item.objectName ?? '',
                }))}
                onItemClick={onChooseNewRule}
                headerVariant={TableHeaderVariant.expandable}
                clearHistory={clearHistory}
                section={section}
              />
            );

          case ConfigurationType.objects:
            return (
              <ObjectsTable
                key={key}
                items={items.map((item) => ({
                  id: item.metadataId,
                  name: item.name,
                  label: item.label,
                  metadataId: item.metadataId,
                  filename: item.filename,
                }))}
                onItemClick={onChooseNewRule}
                headerVariant={TableHeaderVariant.expandable}
                clearHistory={clearHistory}
                section={section}
              />
            );

          case ConfigurationType.cpqData:
            return (
              <CpqTables
                crmOrgId={crmOrgId}
                key={key}
                items={items}
                onItemClick={onChooseNewRule}
                headerVariant={TableHeaderVariant.expandable}
                clearHistory={!!clearHistory}
                activeItemId={activeItemId}
                section={section}
              />
            );

          default:
            return (
              <DefaultConfigurationTable
                headerVariant={TableHeaderVariant.expandable}
                tableHeader={ConfigurationToName[key] ?? key}
                items={items}
                onItemClick={onChooseNewRule}
                key={key}
                parentType={key}
                activeItemId={activeItemId}
                clearHistory={clearHistory}
                section={section}
              />
            );
        }
      };

      const tableContent = createTableContent();

      return <Fragment key={key}>{tableContent}</Fragment>;
    });
  }, [
    configurationKeys,
    configurationItems,
    onChooseNewRule,
    listViewCounter,
    activeItemId,
    clearHistory,
    crmOrgId,
    saltoObjectTypes,
    isLoadingDependency,
    section,
  ]);

  if (!configurationItems) {
    return <></>;
  }

  return (
    <Box
      sx={{
        '& .MuiTableBody-root .MuiTableRow-root': {
          '& .chevron-button': {
            opacity: 0,

            '& > .MuiButton-root': {
              minWidth: 0, //to fix table overflow
            },
          },

          '&:hover .chevron-button': {
            opacity: 1,
          },
        },
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
    >
      <DialogLoader visible={isLoadingDependency} variant="light" />
      {renderedTables}
    </Box>
  );
};
